import type { CSSProperties } from 'react';
import { CheckCircle2, AlertCircle, CircleDashed } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody, Button, Spinner } from '@vnetwork/ui/primitives';
import { formatGiB } from '@/pages/services/oss/storage-format';
import type { UploadItem } from './use-object-upload';

interface Props {
  items: UploadItem[];
  uploading: boolean;
  onClear: () => void;
}

const track: CSSProperties = {
  height: 6,
  borderRadius: 'var(--vn-radius-full)',
  background: 'var(--vn-bg-muted, var(--vn-bg-subtle))',
  overflow: 'hidden',
};

function bar(percent: number, tone: 'accent' | 'success' | 'danger'): CSSProperties {
  const color =
    tone === 'danger'
      ? 'var(--vn-danger-solid)'
      : tone === 'success'
        ? 'var(--vn-success-solid)'
        : 'var(--vn-accent-solid)';
  return {
    width: `${Math.min(percent, 100)}%`,
    height: '100%',
    background: color,
    borderRadius: 'var(--vn-radius-full)',
    transition: 'width 120ms linear',
  };
}

/**
 * Live upload tray for the object browser. Shows an overall "N of M" header with
 * a combined progress bar, then a per-file row (name, size, individual bar or
 * done/error state) so the user always knows how many files are uploading and
 * how far along each one is.
 */
export function ObjectUploadPanel({ items, uploading, onClear }: Props) {
  if (items.length === 0) return null;

  const done = items.filter((it) => it.status === 'done').length;
  const failed = items.filter((it) => it.status === 'error').length;
  const totalBytes = items.reduce((sum, it) => sum + it.size, 0);
  const loadedBytes = items.reduce((sum, it) => sum + it.loaded, 0);
  const overallPct = totalBytes > 0 ? Math.round((loadedBytes / totalBytes) * 100) : 0;

  const header = uploading
    ? `Uploading — ${done} of ${items.length} complete`
    : failed > 0
      ? `Finished — ${done} uploaded, ${failed} failed`
      : `Uploaded ${done} file${done === 1 ? '' : 's'}`;

  return (
    <Card>
      <CardHeader
        actions={
          !uploading && (
            <Button variant="ghost" size="sm" onClick={onClear}>
              Clear
            </Button>
          )
        }
      >
        <CardTitle>
          <span aria-live="polite" aria-atomic="true">{header}</span>
        </CardTitle>
      </CardHeader>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {items.length > 1 && (
          <div
            style={track}
            role="progressbar"
            aria-label="Overall upload progress"
            aria-valuenow={overallPct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div style={bar(overallPct, failed > 0 && !uploading ? 'danger' : uploading ? 'accent' : 'success')} />
          </div>
        )}

        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {items.map((it) => (
            <UploadRow key={it.id} item={it} />
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function UploadRow({ item }: { item: UploadItem }) {
  const pct = item.size > 0 ? Math.round((item.loaded / item.size) * 100) : item.status === 'done' ? 100 : 0;
  const inFlight = item.status === 'uploading' || item.status === 'queued';

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <StatusIcon status={item.status} />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontSize: 'var(--vn-font-size-sm)',
          }}
          title={item.name}
        >
          {item.name}
        </span>
        <span
          style={{
            fontSize: 'var(--vn-font-size-sm)',
            color: item.status === 'error' ? 'var(--vn-danger-text)' : 'var(--vn-fg-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          {item.status === 'error'
            ? item.error ?? 'Failed'
            : item.status === 'done'
              ? formatGiB(item.size)
              : item.status === 'queued'
                ? 'Queued'
                : `${pct}% · ${formatGiB(item.loaded)} / ${formatGiB(item.size)}`}
        </span>
      </div>
      {inFlight && (
        <div
          style={track}
          role="progressbar"
          aria-label={`Uploading ${item.name}`}
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div style={bar(pct, 'accent')} />
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: UploadItem['status'] }) {
  if (status === 'done') return <CheckCircle2 size={15} style={{ color: 'var(--vn-success-solid)', flexShrink: 0 }} />;
  if (status === 'error') return <AlertCircle size={15} style={{ color: 'var(--vn-danger-solid)', flexShrink: 0 }} />;
  if (status === 'uploading') return <Spinner size="sm" />;
  return <CircleDashed size={15} style={{ color: 'var(--vn-fg-muted)', flexShrink: 0 }} />;
}
